// FILE: PropertyDashboard.tsx (Main component combining all logic and presentation)

import React, { useState, useEffect } from 'react';

// =========================================================================
// 1. DATA TYPES
// =========================================================================

/**
 * Defines the structure for the property valuation data.
 */
export interface PropertyValuationData {
  marketValue: number;
  forcedSaleValue: number;
  landValue: number;
  improvementValue: number;
  landAreaSqM: number;
  valuationDate: string;
  valuationId: string;
  regulatoryBody: string;
  principalValuer: string;
  assistantValuer: string;
  professionalCert: string;
  institutionalClient: string;
}

/**
 * Interface for incoming property data
 */
export interface Property {
  upi: string;
  valuationDetails: {
    landValue: number;
    buildingValue: number;
    amenitiesValue: number;
    openMarketValue: number;
    forcedSaleValue: number;
    valuationDate: string;
  };
  valuationTaskDetails: {
    institutionalId: string;
    professionalCert: string;
    principalValuer: string;
    assistantValuer: string;
    regulatoryBody: string;
  };
  landArea: number;
}

/**
 * Interface for institutional client/user
 */
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
}

// =========================================================================
// 2. UTILITY FUNCTIONS
// =========================================================================

const formatCurrency = (amount: number, currency: string = "RWF"): string =>
  `${currency} ${Number(amount || 0).toLocaleString()}`;

const formatArea = (area: number): string => 
  new Intl.NumberFormat('en-US').format(area) + ' m²';

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

// =========================================================================
// 3. KEY METRICS COMPONENT
// =========================================================================

interface KeyMetricsProps {
  data: PropertyValuationData;
  isLoading?: boolean;
  currency?: string;
}

const MetricCard: React.FC<{ title: string; value: string; icon: React.ReactNode; color: string }> = ({ title, value, icon, color }) => (
  <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 ${color} duration-300 hover:shadow-md transition-shadow`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase">{title}</p>
      <span className="text-3xl opacity-50">{icon}</span>
    </div>
    <p className="mt-1  font-bold text-gray-900">{value}</p>
  </div>
);

export const KeyMetrics: React.FC<KeyMetricsProps> = ({ data, isLoading = false, currency = "RWF" }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-gray-300 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            </div>
            <div className="mt-1 h-6 bg-gray-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Institutional Client"
        value={data.institutionalClient || "Not Assigned"}
        icon={<span className="text-green-500">🏦</span>}
        color="border-green-500"
      />

      <MetricCard
        title="Professional Certification"
        value={data.professionalCert || "Not Available"}
        icon={<span className="text-blue-500">📜</span>}
        color="border-blue-500"
      />

      <MetricCard
        title="Valuation Date"
        value={formatDate(data.valuationDate) || "Not Available"}
        icon={<span className="text-purple-500">📅</span>}
        color="border-purple-500"
      />

      <MetricCard
        title="Regulatory Body"
        value={data.regulatoryBody || "Not Available"}
        icon={<span className="text-green-500">🏛️</span>}
        color="border-green-500"
      />

      <MetricCard
        title="Principal Valuer"
        value={data.principalValuer || "Not Assigned"}
        icon={<span className="text-blue-500">👨‍💼</span>}
        color="border-blue-500"
      />

      <MetricCard
        title="Assistant Valuer"
        value={data.assistantValuer || "Not Assigned"}
        icon={<span className="text-purple-500">👩‍💼</span>}
        color="border-purple-500"
      />
    </div>
  );
};

// =========================================================================
// 4. VALUATION BREAKDOWN
// =========================================================================

interface BreakdownProps {
  data: PropertyValuationData;
  currency?: string;
}

export const ValuationBreakdown: React.FC<BreakdownProps> = ({ data, currency = "RWF" }) => {
  const totalValue = data.landValue + data.improvementValue;
  const landPercent = totalValue > 0 ? Math.round((data.landValue / totalValue) * 100) : 0;
  const improvementPercent = totalValue > 0 ? 100 - landPercent : 0;

  return (
    <div className="bg-gray-800 p-6 rounded-xl shadow-2xl text-white">
      <h3 className="text-xl font-bold mb-6 flex items-center">
        <span className="mr-2 text-yellow-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10l9-2 9 2V7l-9-2-9 2zM4 7h16M4 17h16M13 5v14"></path></svg>
        </span>
        Asset Component Distribution+++
      </h3>

      {/* Segmented Bar Chart */}
      <div className="flex w-full h-10 rounded-lg overflow-hidden mb-8 shadow-inner">
        {/* Land Segment */}
        <div 
          style={{ width: `${landPercent}%` }} 
          className="bg-yellow-500 transition-all duration-1000 flex items-center justify-center text-sm font-bold text-gray-900"
        >
          {landPercent > 5 && `${landPercent}%`}
        </div>
        {/* Improvement Segment */}
        <div 
          style={{ width: `${improvementPercent}%` }} 
          className="bg-indigo-500 transition-all duration-1000 flex items-center justify-center text-sm font-bold text-white"
        >
          {improvementPercent > 5 && `${improvementPercent}%`}
        </div>
      </div>

      {/* Legend and Details */}
      <div className="space-y-4">
        <div className="flex justify-between items-center border-b border-gray-700 pb-3">
          <div className="flex items-center">
            <span className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></span>
            <span className="font-semibold text-gray-300">Land Value</span>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-yellow-400">{formatCurrency(data.landValue, currency)}</p>
          </div>
        </div>

        <div className="flex justify-between items-center pt-2">
          <div className="flex items-center">
            <span className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></span>
            <span className="font-semibold text-gray-300">Improvements Value</span>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-indigo-400">{formatCurrency(data.improvementValue, currency)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// 5. VALUATIONS COMPARISON GAUGE
// =========================================================================
interface ValuationGaugeProps {
  data: PropertyValuationData;
  currency?: string;
}

export const ValuationGauge: React.FC<ValuationGaugeProps> = ({ data, currency = "RWF" }) => {
  const marketValue = Math.abs(data.marketValue);
  const forcedSaleValue = Math.abs(data.forcedSaleValue);
  const ratio = marketValue > 0 ? (forcedSaleValue / marketValue) * 100 : 0;
  const percentage = Math.round(ratio);
  
  const maxOffset = 250; 
  const offset = maxOffset - (ratio * 2.5);

  const getColor = (p: number) => {
    if (p > 75) return "#10b981";
    if (p > 65) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-2xl transition-shadow hover:shadow-3xl h-full flex flex-col justify-between">
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <span className="mr-2 text-indigo-500">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
        </span>
        Liquidity & Risk Ratio
      </h3>
      
      <div className="flex justify-center items-center relative h-36">
        <svg 
          width="200" 
          height="120" 
          viewBox="0 0 200 120" 
          className="transform -translate-y-10"
        >
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="20"
            strokeDasharray="250"
            strokeDashoffset="0"
          />
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke={getColor(percentage)}
            strokeWidth="20"
            strokeDasharray="250"
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <text 
            x="100" 
            y="90" 
            textAnchor="middle" 
            className="font-extrabold text-3xl" 
            fill="#1f2937"
          >
            {percentage}%
          </text>
          <text 
            x="100" 
            y="115" 
            textAnchor="middle" 
            className="font-medium text-sm text-gray-500"
            fill="#6b7280"
          >
            FSV / MV Ratio
          </text>
        </svg>
      </div>

      <div className="flex justify-between text-center pt-2 -mt-10 border-t pt-4">
        <div>
          <p className="text-sm font-medium text-gray-500">Market Value</p>
          <p className="text-sm font-bold text-indigo-600">{formatCurrency(data.marketValue, currency)}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">Forced Sale Value</p>
          <p className="text-sm font-bold text-red-600">{formatCurrency(data.forcedSaleValue, currency)}</p>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// 6. MAIN DASHBOARD PAGE
// =========================================================================

interface PropertyDashboardProps {
  property?: Property;
  BASE_URL?: string; // Optional: you can pass the base URL or use a global config
}

const PropertyDashboard: React.FC<PropertyDashboardProps> = ({ property }) => {
  const [institutionalClients, setInstitutionalClients] = useState<User[]>([]);
  const [isClientsLoaded, setIsClientsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch institutional clients
  useEffect(() => {
    const fetchInstitutionalClients = async () => {
      try {
        setIsLoading(true);
        const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";
        const response = await fetch(`${BASE_URL}/users`);
        if (!response.ok) throw new Error("Failed to fetch users");
        const result = await response.json();
        const users: User[] = result.data || [];
        const clients = users.filter(
          (user) => user.role === "INSTITUTIONAL_CLIENT"
        );
        setInstitutionalClients(clients);
        setIsClientsLoaded(true);
      } catch (error) {
        console.error("Error fetching institutional clients:", error);
        setIsClientsLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInstitutionalClients();
  }, []);

  // Function to get institutional client name by ID
  const getInstitutionalClientName = (institutionalId: string): string => {
    if (!isClientsLoaded || !institutionalId) return "Not Assigned";
    
    const client = institutionalClients.find(
      (c) => c.id === institutionalId
    );
    if (client) {
      return `${client.firstName} ${client.lastName}`;
    }
    
    return "Client Not Found";
  };

  // Transform property data or use mock data
  const data: PropertyValuationData = property ? {
    marketValue: property.valuationDetails.openMarketValue || 0,
    forcedSaleValue: property.valuationDetails.forcedSaleValue || 0,
    landValue: property.valuationDetails.landValue || 0,
    improvementValue: (property.valuationDetails.buildingValue || 0) + 
                     (property.valuationDetails.amenitiesValue || 0),
    landAreaSqM: property.landArea || 0,
    valuationDate: property.valuationDetails.valuationDate || "Not Available",
    valuationId: property.upi || "N/A",
    // Get institutional client name using the ID
    institutionalClient: property.valuationTaskDetails?.institutionalId 
      ? getInstitutionalClientName(property.valuationTaskDetails.institutionalId)
      : "Not Assigned",
    professionalCert: property.valuationTaskDetails?.professionalCert || "Not Available",
    regulatoryBody: property.valuationTaskDetails?.regulatoryBody || "Not Available",
    principalValuer: property.valuationTaskDetails?.principalValuer || "Not Assigned",
    assistantValuer: property.valuationTaskDetails?.assistantValuer || "Not Assigned"
  } : {
    marketValue: 86000000,
    forcedSaleValue: 60200000,
    landValue: 18975000,
    improvementValue: 67000000,
    landAreaSqM: 345,
    valuationDate: "2022-01-31",
    valuationId: "UPI: 1/02/01/01/3903",
    institutionalClient: "Institutional Client Name",
    professionalCert: "Professional Certification",
    regulatoryBody: "Regulatory Body",
    principalValuer: "Principal Valuer",
    assistantValuer: "Assistant Valuer"
  };

  // Currency detection based on property country
  const isUganda =
    !!(property as any)?.location?.region ||
    (property as any)?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  return (
    <div className="space-y-6 py-8 py-4 bg-gray-50">
      <section className="mb-10">
        <KeyMetrics data={data} isLoading={isLoading} currency={currency} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <ValuationGauge data={data} currency={currency} />
        </div>
        
        <div className="lg:col-span-2">
          <ValuationBreakdown data={data} currency={currency} />
        </div>
      </section>

      {/* Institutional Client ID Indicator */}
      {/* {property?.valuationTaskDetails?.institutionalId && (
        <div className="mt-4 flex justify-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {isClientsLoaded ? (
              institutionalClients.find(c => c.id === property.valuationTaskDetails.institutionalId) ? (
                `Client ID: ${property.valuationTaskDetails.institutionalId}`
              ) : (
                "Saved ✓"
              )
            ) : (
              "Loading client info..."
            )}
          </span>
        </div>
      )} */}
    </div>
  );
};

export default PropertyDashboard;